import React from 'react';

import clsx from 'classnames';

export type TableColumn<T> = {
  header: string;
  accessor: (row: T) => React.ReactNode;
  className?: string;
};

type TableProps<T> = {
  columns: TableColumn<T>[];
  data: T[];
  getRowKey: (row: T) => string | number;
};

const Table = <T, >({ columns, data, getRowKey }: TableProps<T>) => {
  const isFirstColumn = (index: number) => index === 0;
  const isLastColumn = (index: number) => index === columns.length - 1;

  const getHeaderClassName = (index: number) => {
    return clsx(
      'p-2 font-normal text-gray-600 dark:text-gray-300 text-left paragraph-3',
      isFirstColumn(index) && 'pl-0',
      isLastColumn(index) && 'pr-0',
    );
  };

  const getCellClassName = (column: TableColumn<T>, index: number) => {
    return clsx('p-2 align-middle', isFirstColumn(index) && 'pl-0', isLastColumn(index) && 'pr-0', column.className);
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="border-gray-300 dark:border-gray-700 border-b align-middle">
            {columns.map((column, index) => (
              <th className={getHeaderClassName(index)} key={column.header}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr className="border-gray-300 dark:border-gray-700 border-b" key={getRowKey(row)}>
              {columns.map((column, index) => (
                <td className={getCellClassName(column, index)} key={column.header}>
                  {column.accessor(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
